import {
  flow,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from 'mobx-state-tree';
import { getVideoFileUriByNameIfPresent, trickToStore } from '../../util';
import {
  publicRunsEvents,
  PUBLIC_RUNS_EVENTS,
} from '../../util/publicRunsEvents';

import { Reduced, ST } from '../../types';
import { withEnvironment } from '../extensions/with-environment';
import { logger } from '../../services/Logger';
import _ from 'lodash';

export const _RunModelModel = types
  .model({
    id: types.identifier,
    deviceId: types.string,
    recordId: types.string,
    date: types.Date,
    shareUrl: types.string,
    playUrl: types.maybeNull(types.string),
    videoUrl: types.maybeNull(types.string),
    skater: types.string,
    isPublic: types.optional(types.boolean, false),

    totalScore: types.optional(types.number, 0),
    figuresAchieved: types.optional(types.number, 0),
    figureCount: types.optional(types.number, 0),

    figureScores: types.optional(
      types.array(
        types.model({
          figureIndex: types.number,
          figureName: types.string,
          computedScore: types.number,
          achieved: types.boolean,
          difficultyRating: types.optional(types.number, 0),
        })
      ),
      []
    ),

    nationality: types.maybeNull(types.string),
    latitude: types.maybeNull(types.number),
    longitude: types.maybeNull(types.number),
    location: types.maybeNull(types.string),

    playData: types.maybeNull(types.frozen<ST.Play>()), // not written to firebase storage
    favorite: types.optional(types.boolean, false), // not written to firebase storage
    recordData: types.maybeNull(types.frozen<ST.Record>()), // not written to firebase storage
    localVideoUri: types.maybeNull(types.string), // not written to firebase storage
    localVideoFileName: types.maybeNull(types.string), // not written to firebase storage
  })
  .volatile((_self) => {
    const newFigureNames: string[] = [];
    const newAngles: string[] = [];
    const newHasBackside: string[] = [];
    const newStance: string[] = [];
    const newAchived: string[] = [];
    const newRotations: string[] = [];
    return {
      newFigureNames,
      newAngles,
      newHasBackside,
      newStance,
      newAchived,
      newRotations,
    };
  })
  .extend(withEnvironment)
  .actions((self) => {
    const setScoreData = (scoreData: any) => {
      if (scoreData && scoreData.play_score) {
        self.totalScore = scoreData.play_score.total_score || 0;
        self.figuresAchieved = scoreData.play_score.figures_achieved || 0;
        self.figureCount = scoreData.play_score.figure_count || 0;

        if (scoreData.figure_scores && Array.isArray(scoreData.figure_scores)) {
          self.figureScores.clear();

          scoreData.figure_scores.forEach((figureScore: any, index: number) => {
            self.figureScores.push({
              figureIndex: index,
              figureName: figureScore.figure_name || `Figure ${index + 1}`,
              computedScore: figureScore.computed_score || 0,
              achieved: figureScore.achieved || false,
              difficultyRating: figureScore.difficulty_rating || 0,
            });
          });
        }

        logger.info(`[RunModel] Complete score data set for run ${self.id}`, {
          totalScore: self.totalScore,
          figuresAchieved: self.figuresAchieved,
          figureCount: self.figureCount,
          figureScoresCount: self.figureScores.length,
        });
      }
    };

    const setLocationData = (
      latitude: number,
      longitude: number,
      location?: string,
      nationality?: string | undefined
    ) => {
      self.latitude = latitude;
      self.longitude = longitude;
      if (location) self.location = location;
      if (nationality !== undefined && nationality !== null) {
        self.nationality = nationality;
      }

      logger.info(`[RunModel] Location data set for run ${self.id}`, {
        latitude: self.latitude,
        longitude: self.longitude,
        location: self.location,
        nationality: self.nationality,
      });
    };

    return {
      setScoreData,
      setLocationData,

      afterCreate: flow(function* () {
        if (self.localVideoFileName && !self.localVideoUri) {
          const uri = yield getVideoFileUriByNameIfPresent(
            self.localVideoFileName
          );
          self.localVideoUri = uri;
        }
      }),

      setPlayData(data: ST.Play) {
        self.playData = data;

        if (data.ranking && data.ranking.play_score) {
          setScoreData(data.ranking);
        }

        logger.debug(`[RunModel] Play data set for run ${self.id}`, {
          hasRanking: !!data.ranking,
          hasScore: !!(data.ranking && data.ranking.play_score),
          totalScore: self.totalScore,
          figureScoresCount: self.figureScores.length,
        });
      },

      setRecordData(data: ST.Record) {
        self.recordData = data;
      },

      setPlayURL(url: string) {
        self.playUrl = url;
      },

      setVideoURL(url: string) {
        self.videoUrl = url;
      },

      setLocalVideoName: flow(function* (name: string | null) {
        self.localVideoFileName = name;
        if (name) {
          const uri = yield getVideoFileUriByNameIfPresent(name);
          self.localVideoUri = uri;
        }
      }),

      toggleFavorite() {
        self.favorite = !self.favorite;
      },

      setFavorite(val: boolean) {
        self.favorite = val;
      },

      setShareURL(url: string) {
        self.shareUrl = url;
      },

      setPublic: flow(function* (isPublic: boolean): any {
        self.isPublic = isPublic;
        if (self.shareUrl && self.environment.cloudStorage) {
          try {
            yield self.environment.cloudStorage.updateRun(self as any);
            logger.debug(
              `Successfully synced public status for trick ${self.id}`
            );
          } catch (error) {
            logger.error(
              `Failed to sync public status for trick ${self.id}:`,
              error
            );
            self.isPublic = !isPublic;
            throw error;
          }
        }
      }),

      togglePublic: flow(function* (): any {
        const newPublicStatus = !self.isPublic;
        self.isPublic = newPublicStatus;
        if (self.shareUrl && self.environment.cloudStorage) {
          try {
            yield self.environment.cloudStorage.updateRun(self as any);
            logger.debug(
              `Successfully toggled public status for trick ${self.id} to ${newPublicStatus}`
            );

            if (newPublicStatus) {
              publicRunsEvents.emit(PUBLIC_RUNS_EVENTS.RUN_MADE_PUBLIC, self);
            } else {
              publicRunsEvents.emit(PUBLIC_RUNS_EVENTS.RUN_MADE_PRIVATE, self);
            }

            publicRunsEvents.emit(PUBLIC_RUNS_EVENTS.REFRESH_PUBLIC_RUNS);
          } catch (error) {
            logger.error(
              `Failed to toggle public status for trick ${self.id}:`,
              error
            );
            self.isPublic = !newPublicStatus;
            throw error;
          }
        }
      }),

      downloadPlayData: flow(function* () {
        if (self.playUrl)
          self.playData = (yield self.environment.cloudStorage.downloadPlay(
            self.playUrl
          )).data;
      }),

      downloadVideo: flow(function* () {
        if (self.videoUrl)
          self.localVideoUri =
            yield self.environment.cloudStorage.downloadVideo(self.videoUrl);
      }),

      updatePlayData: flow(function* () {
        if (!self.playUrl || !self.newFigureNames) return;
        const newPlayData = _.cloneDeep(self.playData!);
        self.newFigureNames.forEach((newName, idx) => {
          newPlayData.figures![idx].name = newName;
        });
        if (self.newAngles) {
          self.newAngles.forEach((newAngle, idx) => {
            newPlayData.figures![idx].angle = parseFloat(newAngle);
          });
        }
        if (self.newHasBackside) {
          self.newHasBackside.forEach((newHasBackside, idx) => {
            newPlayData.figures![idx].has_backside = newHasBackside === 'true';
          });
        }
        if (self.newStance) {
          self.newStance.forEach((newStance, idx) => {
            newPlayData.figures![idx].stance = newStance;
          });
        }
        if (self.newAchived) {
          self.newAchived.forEach((newAchived, idx) => {
            newPlayData.figures![idx].achived = newAchived === 'true';
          });
        }
        if (self.newRotations) {
          self.newRotations.forEach((newRotation, idx) => {
            const rotationNumber =
              newRotation === '0'
                ? 0
                : newRotation === 'Double'
                ? 2
                : newRotation === 'Triple'
                ? 3
                : newRotation === 'Quad'
                ? 4
                : 0;

            if (!newPlayData.figures![idx].details) {
              newPlayData.figures![idx].details = {};
            }
            if (!newPlayData.figures![idx].details!.rotation_count_uvw) {
              newPlayData.figures![idx].details!.rotation_count_uvw = {
                x: 0,
                y: 0,
                z: 0,
              };
            }
            newPlayData.figures![idx].details!.rotation_count_uvw!.y =
              rotationNumber;
          });
        }
        self.newFigureNames = [];
        self.newAngles = [];
        self.newHasBackside = [];
        self.newStance = [];
        self.newAchived = [];
        self.newRotations = [];
        self.playData = newPlayData;
        yield self.environment.cloudStorage.uploadPlay(self.playData!);
      }),

      renameFigure(
        idx: number,
        newName: string,
        newAngle: number,
        newHasBackside: boolean,
        newStance: string,
        newAchived: boolean,
        newRotation?: string
      ) {
        if (!self.playData || !self.playData.figures) return;
        self.newFigureNames[idx] = newName;
        self.newAngles[idx] = newAngle.toString();
        self.newHasBackside[idx] = newHasBackside.toString();
        self.newStance[idx] = newStance.toString();
        self.newAchived[idx] = newAchived.toString();
        if (newRotation !== undefined) {
          self.newRotations[idx] = newRotation;
        }
      },

      resetFigureNames() {
        self.newFigureNames = [];
        self.newAngles = [];
        self.newHasBackside = [];
        self.newStance = [];
        self.newAchived = [];
        self.newRotations = [];
      },
    };
  });

type NotInFirebase =
  | 'playData'
  | 'favorite'
  | 'recordData'
  | 'localVideoFileName'
  | 'localVideoUri';

export const RunModelModel = _RunModelModel
  .named('RunModelModel')
  .views((self) => {
    return {
      toServer(uid: string) {
        const x = trickToStore(self as unknown as RunModel);
        x.uid = uid;

        x.totalScore = self.totalScore;
        x.figuresAchieved = self.figuresAchieved;
        x.figureCount = self.figureCount;

        x.figureScores = self.figureScores.map((score) => ({
          figureIndex: score.figureIndex,
          figureName: score.figureName,
          computedScore: score.computedScore,
          achieved: score.achieved,
          difficultyRating: score.difficultyRating,
        }));

        x.nationality = self.nationality;
        x.latitude = self.latitude;
        x.longitude = self.longitude;
        x.location = self.location;

        return x;
      },

      get hasValidScore() {
        return typeof self.totalScore === 'number' && self.totalScore > 0;
      },

      get hasLocationData() {
        return self.latitude !== null && self.longitude !== null;
      },

      get hasFigureScores() {
        return self.figureScores.length > 0;
      },

      getFigureScore(index: number) {
        return self.figureScores.find((score) => score.figureIndex === index);
      },
    };
  });

export type RunModel = Reduced<Instance<typeof RunModelModel>>;
export type RunModelSnapshotIn = SnapshotIn<typeof RunModelModel>;
export type RunModelIn = Reduced<Omit<RunModelSnapshotIn, NotInFirebase>>;
export type RunModelOut = Omit<
  SnapshotOut<typeof RunModelModel>,
  NotInFirebase
>;
